import Link from "next/link";
import { Stack } from "@mui/material";

import { SSText } from "src/components/base/components/SSText";

import { routes } from "src/shared/routes";
import { useSSTheme } from "src/components/reusable/hooks/useSSTheme";
import { useRouterExt } from "src/components/reusable/hooks/useRouterExt";

import type { TTeamConfig } from "src/shared/types/teamConfig.types";

export const FAQS: TTeamConfig["LANGUAGES"]["FAQS"] = [
  {
    question:
      "How do I claim my tickets? When does the opt in open before each game?",
    answer:
      "You will receive a text when the window to check-in is open. The check-in window starts at 9 A.M. Eastern Time on the first game day of a homestand. You will have an opportunity to check-in for all the games in a homestand and purchase upgrades as well at that time (subject to availability). A game's check-in window ends three (3) hours before the respective game's scheduled start time.",
  },
  {
    question:
      "Do I get a ticket to each game on the schedule? Are there penalties if I do not opt in?",
    answer:
      "Upon purchase of the Amazin' Mets Pass, Standing Room Only tickets are guaranteed for each game on the schedule (subject to excluded dates and further exclusion of any additional games at the New York Mets sole discretion). There are no penalties for not attending a game; however, you do need to check-in for a game in order to receive a ticket.",
  },
  {
    question: "Where are my tickets?",
    answer:
      "After checking-in to a game, tickets to the respective game will be delivered to your MLB Ballpark App three (3) hours prior to first pitch. If you are unable to view your tickets, try logging out and back into the MLB Ballpark App.",
  },
  {
    question: "How are tickets sent to me?",
    answer:
      "You will receive a text three (3) hours prior to the start of a game confirming that tickets have been delivered to your MLB Ballpark App.",
  },
  {
    question: "How do I access the MLB Ballpark App?",
    answer: (
      <>
        <b>STEP 1: Download the MLB Ballpark App</b>
        <br />
        <Link
          href="https://mets.com/ballparkapp"
          target="_blank"
        >
          Download the MLB Ballpark App
        </Link>{" "}
        on your iPhone or Android phone and create or log in with your MLB
        account. If you already have the app, be sure to download the latest
        version and keep updating throughout the season.
        <br />
        <br />
        <b>STEP 2: Logging In &amp; Verifying Email</b>
        <br />
        &bull; To log in with your existing MLB account tap &quot;Log In.&quot;
        To create a new account, tap &quot;Sign Up.&quot;
        <br />
        <br />
        &bull; Tap on the silhouette icon at the top of your screen.
        <br />
        <br />
        &bull; Next, tap on the &quot;My MLB Account Emails&quot; button.
        <br />
        <br />
        &bull; If your email is not verified for ticket access tap
        &quot;Resend.&quot; Check your email for verification email from
        MLB.com.
        <br />
        <br />
        &bull; Once your email is verified, a blue check mark will appear next
        to the email address.
        <br />
        <br />
        <b>STEP 3: Locate your Tickets in the Wallet</b>
        <br />
        &bull; All tickets for all games will be located within the Wallet Tab
        of the MLB Ballpark App - please click on the Wallet Tab and locate all
        of your tickets for all games. Your barcode will be accessible beginning
        three (3) hours prior to first pitch of the respective game.
      </>
    ),
  },
  {
    question: "Where will my seats be located?",
    answer:
      "Amazin' Mets Pass tickets only guarantee a Standing Room Only ticket at Citi Field for each eligible game. For select games and at the New York Mets sole discretion, you may be assigned a seated location. A seated location is not guaranteed for any game.",
  },
  {
    question: "Can these tickets be resold or transferred to a friend?",
    answer:
      "Amazin' Mets Pass tickets, as well as seat upgrades, are not eligible for resale. Any attempt to resell Amazin' Mets Pass tickets will result in your Amazin' Mets Pass being revoked. You can forward your Amazin' Mets Pass to family or friends beginning three (3) hours prior to each game via the MLB Ballpark App. Recipients will need to also access and scan their tickets via the MLB Ballpark App.",
  },
  {
    question: "Can my seats be changed?",
    answer: (
      <>
        Once tickets have been sent to your account, they cannot be changed.
        Please contact{" "}
        <Link href="mailto:ticket_services@nymets.com">
          ticket_services@nymets.com
        </Link>{" "}
        or 718-507-TIXX for any assistance you may require.
      </>
    ),
  },
  {
    question: "Can I receive a refund if I cannot attend a game?",
    answer: "We do not issue any refunds for games you are not able to attend.",
  },
  {
    question: "What happens if a game is rained out?",
    answer: (
      <>
        In the event a regulation baseball game (as defined in Official Baseball
        Rule 7.01) is not played due to weather, an act of God or any other
        reason, the following{" "}
        <Link
          href="https://www.mlb.com/mets/ballpark/information/weather"
          target="_blank"
        >
          Raincheck Policy
        </Link>{" "}
        will apply.
      </>
    ),
  },
  {
    question:
      "How do I get into Citi Field with my Amazin' Mets Pass?",
    answer:
      "Your ticket for each game can be accessed exclusively through the Wallet tab within the MLB Ballpark App and your barcodes for each ticket/game will be available beginning three (3) hours prior to each game. Download the MLB Ballpark App and create (or login to a pre-existing) MLB.com account using the same email address you used to purchase your Amazin' Mets Pass. This will provide you with a digital barcode that you can use to scan for admission into Citi Field.",
  },
  {
    question:
      "Am I eligible to receive promotional giveaways with my Amazin' Mets Pass ticket?",
    answer:
      "Yes! With your Amazin' Mets Pass ticket, you are able to enter Citi Field as soon as the gates open, and receive a promotional giveaway item at the gate, provided you are eligible based on that giveaway's posted quantity restrictions.",
  },
  {
    question: "More Information",
    answer: (
      <>
        <b>Digital Ticket Barcode Images</b>
        <br />
        Please note that the barcode area on the digital ticket does not
        automatically refresh. You can refresh the ticket on your screen with a
        simple down-swipe, or by tapping on the &quot;Refresh&quot; icon in the
        middle of the screen. This should refresh and populate the
        ticket&apos;s barcode, as long as it&apos;s within 4 hours of game
        time.
        <br />
        <br />
        <b>Ballpark Re-Entry</b>
        <br />
        Guests who exit the ballpark will not be allowed to re-enter. Guests
        leaving the ballpark for exceptional circumstances may be permitted to
        re-enter, provided they have received approval from a Security or Guest
        Service Supervisor before exiting.
        <br />
        <br />
        <b>Citi Field Information Guide</b>
        <br />
        For more information, please see the{" "}
        <Link
          href="https://www.mlb.com/mets/ballpark/information"
          target="_blank"
        >
          Citi Field Information Guide
        </Link>
        .
      </>
    ),
  },
];

export const PASS_PURCHASE_SUCCESS_MESSAGE: TTeamConfig["LANGUAGES"]["PASS_PURCHASE_SUCCESS_MESSAGE"] =
{
  TITLE: () => (
    <>
      You&apos;e all set to experience the action! Don&apos;t forget to
      confirm attendance here on game day!
    </>
  ),
  DESCRIPTION: () => {
    const router = useRouterExt();
    return (
      <>
        Friends don&apos;t leave friends behind.{" "}
        <b>
          <u>
            <span
              onClick={() => router.pushSchema(routes.my_group)}
              style={{ cursor: "pointer" }}
            >
              Invite your friends
            </span>
          </u>
        </b>{" "}
        and enjoy games together.
      </>
    );
  },
};

export const TERMS_AND_CONDITION = () => (
  <>
    In connection with your purchase, you will receive ticket-related commercial
    emails from{" "}
    <Link href="https://www.mlb.com/mets" target="_blank">
      New York Mets
    </Link>{" "}
    |{" "}
    <Link href="https://www.mlb.com" target="_blank">
      MLB.com
    </Link>
    . You may unsubscribe at any time. By checking this box, you agree to
    receive additional commercial emails from{" "}
    <Link href="https://www.mlb.com/mets" target="_blank">
      New York Mets
    </Link>{" "}
    |{" "}
    <Link href="https://www.mlb.com" target="_blank">
      MLB.com
    </Link>{" "}
    and The Official Site of Major League Baseball. The terms and conditions of
    your ticket and policies applicable to its use are updated to address
    circumstances related to COVID-19 and health and safety. Further, due to the
    COVID-19 pandemic, scheduling may be impacted; games may be canceled,
    rescheduled, or postponed or games may be held with limited capacity. Upon
    completing your purchase by selecting &quot;Purchase Tickets&quot; you
    acknowledge and agree that your credit card will be charged. By clicking
    &quot;Purchase Tickets&quot;, you agree to the{" "}
    <Link
      href="https://www.mlb.com/app/ballpark/official-information/privacy-policy"
      target="_blank"
    >
      MLB Privacy Policy
    </Link>
    ,{" "}
    <Link
      href="https://www.mlb.com/app/ballpark/official-information/terms-of-use"
      target="_blank"
    >
      Terms of Use
    </Link>{" "}
    and to receive a link to your ticket(s) via an SMS text message to the
    mobile # associated with your Amazin&apos; Mets Pass account. Message and
    data rates may apply. Tickets will be made available in the MLB Ballpark
    mobile application only.{" "}
    <Link href="https://www.mlb.com" target="_blank">
      MLB.com
    </Link>{" "}
    and supported mobile device required.
    <br />
    <br />
    Your Amazin&apos; Mets Pass does not include tickets for the following
    games: March 26, March 28, March 29, May 15, May 16, May 17, July 24, July
    25, July 26, and August 1. Additional games may be excluded at the sole
    discretion of the New York Mets at any time. All sales are final. There are
    no refunds or exchanges. Purchase of an Amazin&apos; Mets Pass is subject to
    additional Terms and Conditions.
  </>
);

export const FooterLinks = () => {
  const theme = useSSTheme();
  return (
    <>
      <SSText
        variant="body"
        onClick={() => {
          window.open(
            "https://www.mlb.com/app/ballpark/official-information/privacy-policy",
          );
        }}
        sx={{
          cursor: "pointer",
          color: theme.colors.common.white,
          textDecoration: "underline",
          textUnderlineOffset: "4px",
        }}
      >
        MLB Privacy Policy
      </SSText>
      <SSText
        variant="body"
        onClick={() => {
          window.open(
            "https://www.mlb.com/app/ballpark/official-information/terms-of-use",
          );
        }}
        sx={{
          cursor: "pointer",
          color: theme.colors.common.white,
          textDecoration: "underline",
          textUnderlineOffset: "4px",
        }}
      >
        MLB Terms & Conditions
      </SSText>
    </>
  );
};

export const NavSubmenuLinks = () => {
  const theme = useSSTheme();

  const links = [
    {
      id: 2,
      label: "MLB Privacy Policy",
      onClick: () => {
        window.open(
          "https://www.mlb.com/app/ballpark/official-information/privacy-policy",
          "_self",
        );
      },
    },
    {
      id: 3,
      label: "MLB Terms & Conditions",
      onClick: () => {
        window.open(
          "https://www.mlb.com/app/ballpark/official-information/terms-of-use",
          "_self",
        );
      },
    },
  ];

  return (
    <>
      {links.map((link) => {
        return (
          <Stack
            key={link.id}
            spacing={2}
            direction={"row"}
            alignItems={"center"}
            onClick={() => link.onClick()}
            p={1}
            sx={{
              cursor: "pointer",
              pointerEvents: "auto",
              borderRadius: 2,
              "& .MuiSvgIcon-root": {
                color: theme.colors.gray[200],
              },
              "&:hover": {
                backgroundColor: theme.colors.gray[100],
              },
            }}
          >
            <SSText color={theme.colors.gray[300]}>{link.label}</SSText>
          </Stack>
        );
      })}
    </>
  );
};
